// File: src/components/Analysis/LoadingScreen.js
import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Brain, 
  Search, 
  Calculator, 
  MapPin, 
  CheckCircle,
  Zap,
  Eye,
  DollarSign
} from 'lucide-react';
import styles from './LoadingScreen.module.css';

const LoadingScreen = ({ 
  fileName = 'policy.pdf',
  onComplete,
  estimatedTime = 30000, // 30 seconds
  className = ''
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(estimatedTime / 1000);
  const [insights, setInsights] = useState([]);

  // Processing steps with viral messaging
  const steps = [
    {
      id: 'upload',
      icon: FileText,
      title: 'Reading Your Policy',
      description: 'Scanning every line of your insurance document...',
      duration: 3000,
      color: '--theme-primary'
    },
    {
      id: 'extraction',
      icon: Eye,
      title: 'Extracting Key Details',
      description: 'Finding coverage amounts, deductibles, and premiums...',
      duration: 5000,
      color: '--theme-secondary'
    },
    {
      id: 'analysis',
      icon: Brain,
      title: 'AI Analysis in Progress',
      description: 'Comparing against 47,329+ similar policies...',
      duration: 8000,
      color: '--theme-accent'
    },
    {
      id: 'comparison',
      icon: Search,
      title: 'Neighborhood Comparison',
      description: 'Checking what people in your area actually pay...',
      duration: 6000,
      color: '--theme-primary'
    },
    {
      id: 'calculation',
      icon: Calculator,
      title: 'Calculating Your Grade',
      description: 'Determining if you\'re crushing it or getting crushed...',
      duration: 5000,
      color: '--theme-secondary'
    },
    {
      id: 'complete',
      icon: CheckCircle,
      title: 'Analysis Complete!',
      description: 'Ready to reveal your insurance truth...',
      duration: 3000,
      color: '--theme-accent'
    }
  ];

  // Viral insights that appear during processing
  const viralInsights = [
    "💰 Average user saves $540/year after using Cover Crush",
    "📊 73% of users are overpaying for their current coverage",
    "🏠 Home insurance varies by 40% within the same neighborhood",
    "🚗 Auto insurance can differ by $800+ for identical coverage",
    "⚡ Most people haven't compared rates in over 3 years",
    "🎯 Top 10% of users have A+ ratings and save $1,200+ annually",
    "📈 Insurance rates increased 15% this year - have yours?",
    "🔍 We analyze over 50 data points in your policy"
  ];

  useEffect(() => {
    let stepTimer;
    let progressTimer;
    let countdownTimer;
    let insightTimer;

    // Progress animation
    const updateProgress = () => {
      const totalDuration = steps.reduce((sum, step) => sum + step.duration, 0);
      const currentStepStart = steps.slice(0, currentStep).reduce((sum, step) => sum + step.duration, 0);
      const currentStepProgress = Math.min((Date.now() - stepStartTime) / steps[currentStep]?.duration || 1, 1);
      const overallProgress = ((currentStepStart + (currentStepProgress * (steps[currentStep]?.duration || 0))) / totalDuration) * 100;
      
      setProgress(Math.min(overallProgress, 100));
    };

    // Countdown timer
    const updateCountdown = () => {
      setTimeRemaining(prev => Math.max(prev - 1, 0));
    };

    // Insight rotation
    const showRandomInsight = () => {
      const randomInsight = viralInsights[Math.floor(Math.random() * viralInsights.length)];
      setInsights(prev => {
        const newInsights = [...prev, randomInsight];
        return newInsights.slice(-3); // Keep only last 3 insights
      });
    };

    let stepStartTime = Date.now();

    // Step progression
    const progressToNextStep = () => {
      if (currentStep < steps.length - 1) {
        setCurrentStep(prev => prev + 1);
        stepStartTime = Date.now();
        stepTimer = setTimeout(progressToNextStep, steps[currentStep + 1]?.duration || 3000);
      } else {
        // Analysis complete
        setTimeout(() => {
          onComplete && onComplete();
        }, 2000);
      }
    };

    // Start timers
    stepTimer = setTimeout(progressToNextStep, steps[currentStep]?.duration || 3000);
    progressTimer = setInterval(updateProgress, 100);
    countdownTimer = setInterval(updateCountdown, 1000);
    insightTimer = setInterval(showRandomInsight, 4000);

    // Show first insight immediately
    showRandomInsight();

    return () => {
      clearTimeout(stepTimer);
      clearInterval(progressTimer);
      clearInterval(countdownTimer);
      clearInterval(insightTimer);
    };
  }, [currentStep, onComplete, steps]);

  const formatTime = (seconds) => {
    return `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  const getCurrentStepIcon = () => {
    const StepIcon = steps[currentStep]?.icon || Zap;
    return <StepIcon size={32} />;
  };

  return (
    <div className={`${styles.loadingContainer} ${className}`}>
      {/* Background Effects */}
      <div className={styles.backgroundEffects}>
        <div className={styles.scanningLine}></div>
        <div className={styles.floatingParticles}>
          {[...Array(20)].map((_, i) => (
            <div 
              key={i} 
              className={styles.particle}
              style={{
                animationDelay: `${i * 0.5}s`,
                left: `${Math.random() * 100}%`,
                animationDuration: `${3 + Math.random() * 4}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.content}>
        {/* File Preview */}
        <div className={styles.filePreview}>
          <div className={styles.fileIcon}>
            <FileText size={24} />
          </div>
          <div className={styles.fileName}>{fileName}</div>
          <div className={styles.fileStatus}>Being analyzed by AI...</div>
        </div>

        {/* Current Step Display */}
        <div className={styles.currentStep}>
          <div className={styles.stepIcon}>
            {getCurrentStepIcon()}
          </div>
          <h2 className={styles.stepTitle}>
            {steps[currentStep]?.title || 'Processing...'}
          </h2>
          <p className={styles.stepDescription}>
            {steps[currentStep]?.description || 'Working on your analysis...'}
          </p>
        </div>

        {/* Progress Bar */}
        <div className={styles.progressSection}>
          <div className={styles.progressBar}>
            <div 
              className={styles.progressFill}
              style={{ 
                width: `${progress}%`,
                background: `var(${steps[currentStep]?.color || '--theme-primary'})`
              }}
            />
          </div>
          <div className={styles.progressText}>
            {Math.round(progress)}% Complete
          </div>
        </div>

        {/* Step Indicators */}
        <div className={styles.stepIndicators}>
          {steps.map((step, index) => (
            <div 
              key={step.id}
              className={`
                ${styles.stepIndicator} 
                ${index <= currentStep ? styles.active : ''}
                ${index === currentStep ? styles.current : ''}
              `}
            >
              <div className={styles.stepDot}>
                <step.icon size={16} />
              </div>
              <span className={styles.stepLabel}>{step.title}</span>
            </div>
          ))}
        </div>

        {/* Time Remaining */}
        <div className={styles.timeRemaining}>
          <div className={styles.timeIcon}>⏱️</div>
          <span>Estimated time remaining: {formatTime(timeRemaining)}</span>
        </div>

        {/* Viral Insights */}
        <div className={styles.insightsSection}>
          <h3 className={styles.insightsTitle}>
            <DollarSign size={20} />
            Did You Know?
          </h3>
          <div className={styles.insights}>
            {insights.map((insight, index) => (
              <div 
                key={index}
                className={styles.insight}
                style={{ animationDelay: `${index * 0.5}s` }}
              >
                {insight}
              </div>
            ))}
          </div>
        </div>

        {/* Trust Indicators */}
        <div className={styles.trustBar}>
          <div className={styles.trustItem}>
            <Zap size={16} />
            <span>AI-Powered</span>
          </div>
          <div className={styles.trustItem}>
            <MapPin size={16} />
            <span>Location-Based</span>
          </div>
          <div className={styles.trustItem}>
            <CheckCircle size={16} />
            <span>Secure & Private</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;